/**
 * LightTargetPicker - the one control that answers "which lights?".
 *
 * Shared by all three targeted lighting items, which is the point: "point
 * this at the wash group" has to be the same gesture whether the thing being
 * pointed is a colour button, a level fader or an effect. Three near-identical
 * pickers is how two of them drift.
 *
 * ## One select, not two
 *
 * The obvious build is a KIND select ("All / Group / Fixture / Zone") plus a
 * second select for the value, cascading the way the Rig State bank/look
 * pickers do. That is the wrong shape here and the reason is worth stating,
 * because it looks like consistency to copy it:
 *
 * A Rig State bank and a look are two genuinely nested ideas - you cannot
 * name a look without naming a bank. "All lights", "the wash group" and "Back
 * Left" are not nested; they are four items on ONE list of things the
 * operator might mean. A kind select would make the novice choose a
 * TAXONOMY before choosing a light, which is exactly the "spend time figuring
 * it out" cost SUITE-FACTS §0 exists to remove. So this is one flat select
 * with `<optgroup>` headers, and the first option is the one most people
 * want.
 *
 * ## Ordering is by how often it is reached for, not alphabetically
 *
 * All lights, then groups, then fixtures, then zones under their fixture.
 * A novice picking their first target lands on "All lights" without
 * scrolling; an operator who has authored groups finds them above the raw
 * fixture list, because a group is the thing they made precisely so they
 * would not have to pick fixtures one at a time.
 *
 * ## An unresolvable target stays SELECTED and says so
 *
 * If the fixture a control points at has been deleted, the select does not
 * silently fall back to "All lights" - that would turn a broken button into a
 * working one aimed at the whole rig, which on stage is worse than a dead
 * button. The stale value is kept as its own disabled option reading "light
 * removed", so the operator can see what happened and repoint it.
 */

import { For, Show } from 'solid-js';
import type { DmxTarget } from '@opbuttons/schema';
import { fixtures, fixtureZones } from '../fixtures/store.ts';
import { allGroups, describeTarget, targetKey, targetIsLive } from '../lighting/targets.ts';
import { bridgeFixtureStatus } from '../fixtures/bridgeSync.ts';

interface LightTargetPickerProps {
  target: DmxTarget;
  onChange: (next: DmxTarget) => void;
  /** Opens the group editor. Omitted on surfaces that should not offer it. */
  onManageGroups?: () => void;
  /** Offer whole lights only, no zones. For a Light Preset, whose contract
   *  (`POST /lighting/fire`) takes fixture ids: a preset paints a zoned bar
   *  zone by zone on its own, so a zone is not a thing it can be aimed at. */
  wholeLightsOnly?: boolean;
}

/** Encode a target as a select `value`, and back. Same encoding as
 *  `targetKey` so a stale selection can be matched by string without
 *  re-deriving the parse in two places. */
function parseKey(key: string): DmxTarget | null {
  if (key === 'all') return { kind: 'all' };
  if (key.startsWith('group:')) return { kind: 'group', group: key.slice(6) };
  if (key.startsWith('fixture:')) return { kind: 'fixture', fixtureId: key.slice(8) };
  if (key.startsWith('zone:')) {
    const rest = key.slice(5);
    const ix = rest.lastIndexOf(':');
    if (ix <= 0) return null;
    const fixtureId = rest.slice(0, ix);
    const subfixture = Number(rest.slice(ix + 1));
    if (!Number.isFinite(subfixture)) return null;
    return { kind: 'zone', fixtureId, subfixture };
  }
  return null;
}

export function LightTargetPicker(props: LightTargetPickerProps) {
  const groups = () => allGroups();
  const rig = () => fixtures();
  const zoned = () => (props.wholeLightsOnly ? [] : rig().filter((f) => fixtureZones(f).length > 0));
  const stale = () => !targetIsLive(props.target);

  return (
    <>
      <label class="inspector-field">
        <span>Lights</span>
        <select
          value={targetKey(props.target)}
          onChange={(e) => {
            const next = parseKey(e.currentTarget.value);
            if (next) props.onChange(next);
          }}
        >
          {/* The stale selection, kept so the operator sees WHAT broke rather
              than finding the control silently re-aimed at the whole rig. */}
          <Show when={stale()}>
            <option value={targetKey(props.target)} disabled>
              {describeTarget(props.target)}
            </option>
          </Show>

          <option value="all">All lights{rig().length ? ` (${rig().length})` : ''}</option>

          <Show when={groups().length > 0}>
            <optgroup label="Groups">
              <For each={groups()}>
                {(g) => {
                  const n = rig().filter((f) => (f.groups ?? []).includes(g)).length;
                  return <option value={`group:${g}`}>{g} ({n})</option>;
                }}
              </For>
            </optgroup>
          </Show>

          <Show when={rig().length > 0}>
            <optgroup label="Lights">
              <For each={rig()}>
                {(f) => <option value={`fixture:${f.id}`}>{f.name}</option>}
              </For>
            </optgroup>
          </Show>

          {/* Zones live in their own group rather than nested under each
              fixture, because a flat select has no nesting to offer and
              prefixing every entry with its fixture name keeps them readable
              in one list: "COLORband PiX - Block 3". */}
          <For each={zoned()}>
            {(f) => (
              <optgroup label={`${f.name} - zones`}>
                <For each={fixtureZones(f)}>
                  {(z) => (
                    <option value={`zone:${f.id}:${z.index}`}>{f.name} - {z.name}</option>
                  )}
                </For>
              </optgroup>
            )}
          </For>
        </select>
      </label>

      {/* An empty rig is not a broken picker, and the three reasons it can be
          empty have three different fixes - so the hint names which one it is
          rather than saying "no lights" and leaving the operator to guess
          between "I haven't patched any" and "the bridge is down". */}
      <Show when={rig().length === 0}>
        <div class="inspector-hint">
          {bridgeFixtureStatus() === 'no-bridge'
            ? 'No OPBridge connected, so no rig to read. Connect one to pick lights.'
            : bridgeFixtureStatus() === 'forbidden'
              ? 'OPBridge will not share its rig with this pairing (missing scope).'
              : bridgeFixtureStatus() === 'ready'
                ? 'OPBridge has no lights patched yet.'
                : 'Could not read the lighting rig from OPBridge.'}
        </div>
      </Show>

      <Show when={props.onManageGroups && rig().length > 0}>
        <button
          type="button"
          class="inspector-pill"
          onClick={() => props.onManageGroups?.()}
          title="Tag lights into groups so you can aim one control at several"
        >
          {groups().length ? 'Edit groups…' : 'Make a group…'}
        </button>
      </Show>
    </>
  );
}
